import React from 'react';
import { Category, TestResult } from '../../types';

export interface TestManagerSidebarProps {
  categories: Category[];
  currentCategory: string;
  currentTest: TestResult;
  onCategorySelect: (categoryName: string) => void;
  onClearScenario: () => void;
  onDeleteCategory?: (categoryName: string) => void;
  onCategoryDeleteClick: (categoryName: string, event: React.MouseEvent) => void;
}

export const TestManagerSidebar: React.FC<TestManagerSidebarProps> = ({
  categories,
  currentCategory,
  currentTest,
  onCategorySelect,
  onClearScenario,
  onDeleteCategory,
  onCategoryDeleteClick,
}) => {
  const filteredCategories = categories.filter((category) => {
    if (currentTest.metadata?.isFullRegression) return true;
    if (currentTest.metadata?.selectedCategories?.length) {
      return currentTest.metadata.selectedCategories.includes(category.name);
    }
    return true;
  });

  return (
    <div className="col-span-12 md:col-span-3 bg-white dark:bg-slate-700/80 rounded-2xl shadow-xl p-6 transform transition-all duration-200 hover:shadow-2xl">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10" />
        </svg>
        Kategorie
      </h2>
      <div className="space-y-2">
        {filteredCategories.map((category) => {
          const allTestsCompleted = category.scenarios.every((s) => s.Status && s.Status !== 'Not Tested');
          const completedCount = category.scenarios.filter((s) => s.Status && s.Status !== 'Not Tested').length;
          const totalCount = category.scenarios.length;
          const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

          let progressColor = '';
          if (progressPercentage === 100) progressColor = 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400';
          else if (progressPercentage >= 75) progressColor = 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400';
          else if (progressPercentage >= 50) progressColor = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400';
          else if (progressPercentage > 0) progressColor = 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400';
          else progressColor = 'bg-gray-100 text-gray-700 dark:bg-slate-600 dark:text-gray-100';

          const passedCount = category.scenarios.filter((s) => s.Status === 'Passed').length;
          const failedCount = category.scenarios.filter((s) => s.Status === 'Failed').length;
          const blockedCount = category.scenarios.filter((s) => s.Status === 'Blocked').length;
          const notTestedCount = category.scenarios.filter((s) => !s.Status || s.Status === 'Not Tested').length;

          return (
            <div
              key={category.name}
              className={`w-full rounded-xl transition-all duration-200 relative group ${
                currentCategory === category.name ? 'bg-blue-50 dark:bg-blue-900/30 shadow-md' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                <button
                  onClick={() => {
                    onCategorySelect(category.name);
                    onClearScenario();
                  }}
                  className={`flex-1 text-left px-4 py-3 rounded-xl transition-all duration-200 flex flex-col sm:flex-row sm:items-center justify-between ${
                    currentCategory === category.name
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-md'
                      : 'hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  <div className="flex flex-col w-full sm:w-auto">
                    <div className="flex items-center gap-2 mb-2 sm:mb-0">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <div className="flex items-center gap-2">
                        <div>{category.name}</div>
                        {category.scenarios.some((s) => s.Status === 'Failed') && (
                          <div className="text-red-500" title="W tej kategorii znajdują się niezaliczone testy">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-full sm:w-32 h-1.5 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden mt-1.5">
                      <div
                        className={`h-full rounded-full ${
                          progressPercentage === 100
                            ? 'bg-green-500'
                            : progressPercentage >= 75
                              ? 'bg-blue-500'
                              : progressPercentage >= 50
                                ? 'bg-yellow-500'
                                : progressPercentage > 0
                                  ? 'bg-orange-500'
                                  : 'bg-gray-300'
                        }`}
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center mt-2 sm:mt-0">
                    {allTestsCompleted && (
                      <span className="mr-2 text-green-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${progressColor}`}>
                      {progressPercentage.toFixed(2)}%
                    </span>
                  </div>
                </button>
                {onDeleteCategory && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCategoryDeleteClick(category.name, e);
                    }}
                    className="mt-3 p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 border border-transparent hover:border-red-200 dark:hover:border-red-800 rounded transition-all duration-200 flex items-center justify-center flex-shrink-0 opacity-60 hover:opacity-100"
                    title="Usuń całą kategorię"
                  >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  </button>
                )}
              </div>
              <div className="absolute z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-black bg-opacity-80 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                  <span>Zaliczone: {passedCount}</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Niezaliczone: {failedCount}</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span>Zablokowane: {blockedCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-gray-500" />
                  <span>Nietestowane: {notTestedCount}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
